/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import CDSCheckbox from '@carbon/web-components/es/components/checkbox/checkbox.js';
import FocusMixin from '@carbon/web-components/es/globals/mixins/focus.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import '@carbon/web-components/es/components/modal/modal.js';
import styles from './filter-panel.scss';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { stablePrefix: c4dPrefix } = settings;

/**
 * Filter Panel Checkbox
 *
 * @element c4d-filter-panel-checkbox
 * @fires c4d-checkbox-select - The custom event fired after this changebox changes its checked state.
 */
@customElement(`${c4dPrefix}-filter-panel-checkbox`)
class C4DFilterPanelCheckbox extends FocusMixin(
  StableSelectorMixin(CDSCheckbox)
) {
  /**
   * Handles `click` event on the `<input>` in the shadow DOM.
   */
  protected _handleChange() {
    const { checked, indeterminate } = this._checkboxNode;
    this.checked = checked;
    this.indeterminate = indeterminate;
    const { eventChange } = this.constructor as typeof CDSCheckbox;
    this.dispatchEvent(
      new CustomEvent(eventChange, {
        bubbles: true,
        composed: true,
        detail: {
          value: this.value,
        },
      })
    );
  }

  /**
   * The name of the event that fires upon checkbox is selected
   */
  static get eventChange() {
    return `${c4dPrefix}-checkbox-select`;
  }

  static get stableSelector() {
    return `${c4dPrefix}-filter-panel-checkbox`;
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DFilterPanelCheckbox;
